import React, { FC, useMemo, useState } from "react";
import {
  Input,
  Form,
  Button,
  Divider,
  Checkbox,
} from "antd";
import { useParams } from "react-router-dom";
import { useForm } from "antd/es/form/Form";
import { ruleRequired } from "../../../../../../constants/rules/RulesConstantsForms";
import { ruleLettersOnly, ruleAddress } from "../../../../../../constants/rules/RulesConstantsUser";
import { useGetApplicationDataToBankQuery } from "../../../../../../store/api/applicationBankApi/applicationBank.api";
import { UI_ITONumericInput } from "../../../../../../UI_ITO/UI_ITONumericInput/UI_ITONumericInput";
import { BANK_NAME, APPLICATION_BANK_STATUS_KKB } from "../../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { Guard } from "../../../../../../utils/guards/Guard";
import { ILegalEntitieProps } from "../../../../../../interfaces/props/ILegalEntitieProps";

import classes from "./LegalEntitieInfo.module.css";
import { UI_ITODatePicker } from "../../../../../../UI_ITO/UI_ITODatePicker/UI_ITODatePicker";
import UI_ITOCodeDivisionInput from "../../../../../../UI_ITO/UI_ITOCodeDivisionInput/UI_ITOCodeDivisionInput";
import { validatePassportAddressIndex, validatePassportDivisionCode, validatePassportNumber, validatePassportSeries } from "../../../../../../utils/utilities/validateDataToBank";
import { ruleInnIndividual } from "../../../../../../constants/rules/RulesConstantsApplication";
import { maxLengthInn, maxLengthPassporDefaultLength, maxLengthPassportDivisionCode, maxLengthPassportNumber, maxLengthPassportPostIndex, maxLengthPassportSeries, maxLengthSnils } from "../../../../../../constants/maxLengthInFolders";

export const LegalEntitieInfo: FC<ILegalEntitieProps> = (props) => {
  const [isAddressChecked, setIsAddressChecked] = useState<boolean>( false );
  const { applicationBankId, applicationId } = useParams();
  const [form] = useForm();
  const initialValues = useMemo(() => {
    const [ _, surname, name, patronymic ] = props.shortname.split(' ');
    
    return {
      address_legal: props.address_legal,
      name: props.name,
      inn: props.inn,
      percent: props.percent,
      registration_place: props.registration_place,
      okopf: props.okopf,
      okfs: props.okfs,
      ogrn: props.ogrn,
      okpo: props.okpo,
      directorSurname: surname,
      directorName: name,
      directorPatronymic: patronymic,
      directorBirtday: '',
      dateRegistration: props.date_registration,
      directorPassportSeria: '',
      directorPassportNumber: '',
      directorPassportCode: '',
      directorPassportIssueDate: '',
      directorInn: '',
      directorSnils: '',
      directorPassportDepartment: '',
      directorPlaceBirth: '',
      directorPlaceBirthPostIndex: '',
      directorPostAndRegistrationAddress: ''
    };
  }, [props]);

  const { data: applicationBankData } = useGetApplicationDataToBankQuery({
    applicationId: Number(applicationId),
    applicationBankId: Number(applicationBankId),
  });

  return (
    <div className={classes.legalEntitieInfoContainer}>
      <Divider
        orientation="left" 
        orientationMargin="0"
        className={classes.legalEntitieDivider}
      >
        Информация об организации
      </Divider>
      <Form
        layout="vertical"
        name={`individual_information_form_${props.id}`}
        initialValues={initialValues}
        form={form}
        autoComplete="off"
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        <Form.Item
          label="Наименование организации"
          rules={[
            ruleRequired,
            ruleLettersOnly,
          ]}
          name="name"
        >
          <Input placeholder="Введите наименование организации" size="large" />
        </Form.Item>

        <div className={classes.formInputsGroupWrapper}>
          <Form.Item
            label="ИНН организации"
            name="inn"
            rules={[ruleInnIndividual, ruleRequired]}
          >
            <UI_ITONumericInput placeholder="Введите ИНН организации" size="large" maxLength={maxLengthInn} />
          </Form.Item>
         
          <Form.Item
            label="ОГРН"
            name="ogrn"
            rules={[ruleRequired]}
          >
            <UI_ITONumericInput placeholder="Введите ОГРН" size="large" maxLength={maxLengthPassporDefaultLength} />
          </Form.Item>

           <Form.Item
            label="ОКОПФ"
            name="okopf"
            rules={[ruleRequired]}
          >
            <UI_ITONumericInput placeholder="Введите ОКОПФ" size="large" maxLength={maxLengthPassporDefaultLength} />
          </Form.Item>

          <Form.Item
            label="ОКПО"
            name="okpo"
            rules={[ruleRequired]}
          >
            <UI_ITONumericInput placeholder="Введите ОКПО" size="large" maxLength={maxLengthPassporDefaultLength} />
          </Form.Item>

          <Form.Item
            label="ОКТМО"
            name="opf_name"
            rules={[ruleRequired]}
          >
            <Input placeholder="Введите ОКТМО" size="large" maxLength={maxLengthPassporDefaultLength} />
          </Form.Item>

          <Form.Item
            label="ОКФС"
            name="okfs"
            rules={[ruleRequired]}
          >
            <Input placeholder="Введите ОКФС" size="large" maxLength={maxLengthPassporDefaultLength} />
          </Form.Item>
        </div>

        <div className={classes.formInputsWrapper}>
          <Form.Item
            label="Адрес регистрации"
            name="registration_place"
            rules={[ruleAddress, ruleRequired]}
          >
            <Input placeholder="Введите адрес регистрации" size="large" />
          </Form.Item>


          <Form.Item
            label="Адрес фактического местоположения"
            name="address_legal"
            rules={[ruleAddress, ruleRequired]}
          >
            <Input placeholder="Введите адрес фактического местоположения" size="large" />
          </Form.Item>
        </div>

        <Divider
          orientation="left" 
          orientationMargin="0"
          className={classes.legalEntitieDivider}
        >
          Информация о бенефициаре
        </Divider>

        <div className={classes.formInputsWrapper}>
          <Form.Item
            label="Фамилия"
            rules={[
              ruleRequired,
              ruleLettersOnly,
            ]}
            name="directorSurname"
          >
            <Input placeholder="Введите фамилию" size="large" />
          </Form.Item>

          <Form.Item
            label="Имя"
            rules={[
              ruleRequired,
              ruleLettersOnly,
            ]}
            name="directorName"
          >
            <Input placeholder="Введите имя" size="large" />
          </Form.Item>

          <Form.Item
            label="Отчество"
            rules={[
              ruleRequired,
              ruleLettersOnly,
            ]}
            name="directorPatronymic"
          >
            <Input placeholder="Введите отчество" size="large" />
          </Form.Item>

          <Form.Item
            label="Дата рождения"
            name="directorBirtday"
            rules={[
              ruleRequired,
              ruleLettersOnly,
            ]}
          >
            <UI_ITODatePicker
              placeholder="Введите дату рождения"
              style={{ width: "100%" }}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Серия"
            rules={[
              ruleRequired,
              { validator: validatePassportSeries }
            ]}
            name="directorPassportSeria"
          >
            <UI_ITONumericInput maxLength={maxLengthPassportSeries} placeholder="Введите серию пасспорта" size="large" />
          </Form.Item>

          <Form.Item
            label="Номер"
            rules={[
              ruleRequired,
              { validator: validatePassportNumber }
            ]}
            name="directorPassportNumber"
          >
            <UI_ITONumericInput maxLength={maxLengthPassportNumber} placeholder="Введите номер пасспорта" size="large" />
          </Form.Item>


          <Form.Item
            label="Код подразделения"
            rules={[
              ruleRequired,
               { validator: validatePassportDivisionCode }
            ]}
            name="directorPassportCode"
          >
            <UI_ITOCodeDivisionInput maxLength={maxLengthPassportDivisionCode} type="tel" placeholder="Введите отчество" size="large" />
          </Form.Item>

          <Form.Item
            name="directorPassportIssueDate"
            label="Дата выдачи"
            rules={[ruleRequired]}
          >
            <UI_ITODatePicker
              size="large"
              style={{ width: "100%" }}
              placeholder="Введите дату выдачи"
            />
          </Form.Item>

          <Form.Item
            label="ИНН"
            rules={[
              ruleRequired,
              ruleInnIndividual,
            ]}
            name="directorInn"
          >
            <UI_ITONumericInput placeholder="Введите ИНН" maxLength={maxLengthInn} size="large" />
          </Form.Item>

          <Form.Item
            label="СНИЛС"
            rules={[
              ruleRequired,
              ruleLettersOnly,
            ]}
            name="directorSnils"
          >
            <UI_ITONumericInput placeholder="Введите снилс" size="large" maxLength={maxLengthSnils} />
          </Form.Item>
        </div>

        <Form.Item
          label="Кем выдан"
          rules={[
            ruleRequired,
            ruleAddress,
          ]}
          name="directorPassportDepartment"
        >
          <Input placeholder="Введите кем выдан" size="large" />
        </Form.Item>

        <Form.Item
          label="Место рождения"
          rules={[
            ruleRequired,
            ruleAddress,
          ]}
          name="directorPlaceBirth"
        >
          <Input placeholder="Введите место рождения" size="large" />
        </Form.Item>



        <div className={classes.formInputsWrapperSecond}>
          <Form.Item
            label="Индекс"
            name="directorPlaceBirthPostIndex"
            rules={[ruleRequired, { validator: validatePassportAddressIndex }]}
          >
            <UI_ITONumericInput size="large" placeholder="Введите индекс" maxLength={maxLengthPassportPostIndex} />
          </Form.Item>

          <Form.Item
            label="Адрес регистрации"
            name="directorPassportAddress"
            rules={[ruleAddress, ruleRequired]}
          >
            <Input size="large" />
          </Form.Item>
        </div>


         <Form.Item valuePropName="checked" name="directorPostAndRegistrationAddress">
            <Checkbox onChange={() => setIsAddressChecked((prev) => !prev)} className={classes.legalEntitieInfoCheckbox}>
              Адрес регистрации совпадает с местом жительства
            </Checkbox>
          </Form.Item>

        <div className={classes.buttonWrapper}>
          <Form.Item>
            {applicationBankData && (
              <>
                <Guard.ApplicationBankStatus
                  status={applicationBankData?.status}
                  allowedStatuses={[
                    {
                      bank_name: BANK_NAME.KKB,
                      type: APPLICATION_BANK_STATUS_KKB.PROCESSING,
                    },
                    {
                      bank_name: BANK_NAME.KKB,
                      type: APPLICATION_BANK_STATUS_KKB.REVISION,
                    },
                  ]}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                  >
                    Сохранить изменения
                  </Button>
                </Guard.ApplicationBankStatus>
              </>
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};
